import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dimmer, Icon, Loader } from 'semantic-ui-react';
import FarmerGroupsService from '~/api/services/farmerGroups';
import { FormFields } from '~/components/FormFields';
import notify from '~/components/Notify';
import FarmerGroupQuery from '~/QueryHooks/FarmerGroupsQuery';
import { useAuth } from '~/routes/components/AuthProvider';
import { InputWrapper } from './style';
import { handleErrors } from '~/utils/helpers';
import Drawer from '~/components/Drawer';
import { UserEl } from '../Users/style';
import { GlobalEl } from '~/styles/app';
import { tradingSlotModules } from '~/Constants';

interface Props {
  isVisible: boolean
  id: string
  handleHiddenDrawer: (loadNewFarmerGroups?: boolean) => void
}

const defaultValues = {
  name: '',
  uniqueId: '',
  mgaFactor: '',
  description: '',
  modules: [],
  amountPercent: '',
  minimumContingent: '',
  terms: []
}

const CreateEditFarmerGroups: React.FC<Props> = (props) => {
  const { id, isVisible, handleHiddenDrawer } = props
  const [isBusy, setIsBusy] = useState(false)
  const { t } = useTranslation()
  const [loadingFarmerGroupName, setLoadingFarmerGroupName] = useState(false)
  const [correctFarmerGroupName, setCorrectFarmerGroupName] = useState(true)
  const { systemData: { amountUnit, modules } } = useAuth()

  const { handleSubmit, control, reset, watch, formState: { dirtyFields }, setValue } = useForm({
    mode: 'onSubmit'
  })
  const watchFarmerGroupName = watch('name')
  const watchDescription = watch('description')
  const uniqueFarmerGroupMessage = t('Please enter unique farmer group name')
  const hasAnyTradingSlot = Boolean(modules.find(module => !!tradingSlotModules.find(tradingSlotSlug => tradingSlotSlug === module.slug)))
  const watchGroupModule = watch('modules') || []

  const {
    data: farmerGroupsOptions,
    refetch: fetchOptions,
    isLoading: isBusyOptions
  } = FarmerGroupQuery.getFarmerGroupsOptionData()

  useEffect(() => {
    if (id !== 'create' && isVisible) {
      fetchFarmerGroupData()
    } else if (id === 'create' && isVisible) {
      reset(defaultValues)
    }
  }, [id, isVisible])

  useEffect(() => {
    if (id !== 'create' && isVisible) {
      fetchOptions()
    }
  }, [t, id, isVisible])

  async function fetchFarmerGroupData() {
    setIsBusy(true)
    try {
      const response = await FarmerGroupsService.getFarmerGroup(id)
      response.data.terms = [...new Set(response.data.terms)]
      reset(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function onSubmit(data) {
    if (data.modules) {
      data.modules = data.modules.filter(module => !!module)
    }
    if (data.terms) {
      data.terms = data.terms.filter(term => !!term)
    }
    data.amountPercent = parseInt(data.amountPercent)
    setIsBusy(true)
    try {
      if (id !== 'create') {
        await FarmerGroupsService.updateFarmerGroup(id, data)
        notify(t('Farmer group updated'), 'success');
      } else {
        await FarmerGroupsService.createFarmerGroup(data)
        notify(t('Farmer group created'), 'success');
      }
      handleHiddenDrawer(true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function checkFarmerGroupName(value) {
    setValue('name', value)
    setLoadingFarmerGroupName(true)
    try {
      const response = await FarmerGroupsService.checkFarmerGroupName({ name: value, id })
      setCorrectFarmerGroupName(Boolean(response.data))
    } catch (error) {
      handleErrors(error)
    }
    setLoadingFarmerGroupName(false)
  }

  function renderForm() {
    const tradingModules = farmerGroupsOptions?.modules?.filter(option => Boolean(tradingSlotModules.find(tradingSlotSlug => tradingSlotSlug === option.slug)))
    const groupHasTradingModuleSelect = Boolean(watchGroupModule.find(moduleId => Boolean(tradingModules.find(trading => moduleId === trading.id))))
    return <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2em' }}>
      <FormFields.Input
        control={control}
        label={'ID'}
        placeholder={'ID'}
        name={'uniqueId'}
        rules={{
          maxLength: {
            value: 10,
            message: t('Max number of char. is 10')
          }
        }}
      />
      <InputWrapper>
        <FormFields.Input
          control={control}
          placeholder={t('Name')}
          label={t('Name')}
          name={'name'}
          onChange={(e, data) => checkFarmerGroupName(data.value)}
          rules={{
            required: {
              value: true,
              message: t('Please enter farmer group name')
            },
            validate: () => correctFarmerGroupName || uniqueFarmerGroupMessage
          }}
        />
        <div className='loaderWrapper'>
          {loadingFarmerGroupName && <Dimmer inverted active={loadingFarmerGroupName}>
            <Loader size='small' active={loadingFarmerGroupName} />
          </Dimmer>}
        </div>
        {!loadingFarmerGroupName && watchFarmerGroupName !== '' && dirtyFields?.name && <Icon
          style={{ marginLeft: 10 }}
          name={correctFarmerGroupName ? 'checkmark' : 'close'}
          color={correctFarmerGroupName ? 'green' : 'red'}
        />}
      </InputWrapper>
      {farmerGroupsOptions?.mgaFactors?.length > 0 && <FormFields.Select
        label={t('MGA factor')}
        placeholder={t('Select MGA factor')}
        control={control}
        name={'mgaFactor'}
        options={farmerGroupsOptions?.mgaFactors.map(mgaFactor => ({ value: mgaFactor.slug, text: mgaFactor.label }))}
      />
      }
      <FormFields.TextArea
        control={control}
        label={t('Description')}
        name={'description'}
        style={{ marginBottom: 0 }}
        rules={{
          maxLength: {
            value: 1000,
            message: t('Max amount of characters is 1000')
          }
        }}
      />
      <small style={{ float: 'right' }}>{`Characters: ${watchDescription?.length || 0} (max. 1000)`}</small>
      <FormFields.Select
        multiple
        selection
        options={farmerGroupsOptions?.modules.map(module => ({ value: module.id, text: module.label }))}
        label={t('Modules')}
        placeholder={t('Modules')}
        control={control}
        name={'modules'}
      />
      {hasAnyTradingSlot && groupHasTradingModuleSelect && <>
        <FormFields.Input
          typeInput={'controled'}
          control={control}
          rules={{
            required: {
              value: true,
              message: t('Please enter amount %')
            },
            min: {
              value: 1,
              message: t('Please enter positive number greater then 0')
            },
            max: {
              value: 100,
              message: t('Trading amount can’t be more than 100%')
            }
          }}
          allowDecimals={false}
          name='amountPercent'
          placeholder={t('Trading amount (% / year)')}
          label={t('Trading amount (% / year)')}
        />
        <FormFields.Input
          typeInput={'controled'}
          label={t('Minimum contingent (in {{amountUnit}})', { amountUnit })}
          placeholder={t('Minimum contingent (in {{amountUnit}})', { amountUnit })}
          type={'number'}
          allowDecimals={false}
          rules={{
            required: {
              value: true,
              message: t('Please enter minimum contingent')
            },
            min: {
              value: 1,
              message: t('Please enter positive number greater then 0')
            }
          }}
          name={'minimumContingent'}
          control={control}
        />
        {farmerGroupsOptions?.terms?.length > 0 && !isBusy && <FormFields.Select
          control={control}
          multiple
          options={farmerGroupsOptions.terms.map(term => ({
            value: term.id, text: term.title
          }))}
          selection
          label={t('Select trading room')}
          placeholder={t('Select terms')}
          name={'terms'}
        />
        }
      </>}
      <UserEl.DrawerFooter>
        <GlobalEl.PrimaryButton
          disabled={!correctFarmerGroupName || Object.keys(dirtyFields).length === 0}
        >{id !== 'create' ? t('Update') : t('Add')}</GlobalEl.PrimaryButton>
      </UserEl.DrawerFooter>
    </div>
  }

  return <Drawer
    isBusy={isBusyOptions || isBusy}
    content={isVisible && renderForm()}
    header={id !== 'create' ? t('Update farmer group') : t('Create a new farmer group')}
    onHide={() => {
      reset();
      handleHiddenDrawer();
    }}

    isVisible={isVisible}
    handleSubmit={handleSubmit(onSubmit)}
    closeOnEscape
  />
}
export default CreateEditFarmerGroups
